<script>
    import Breadcrumbs from '$lib/Breadcrumbs.svelte';
    import Card from '$lib/Card.svelte';
    import Faq2 from '$lib/FAQ2.svelte';
    import H1 from '$lib/H1.svelte';
    import H2 from '$lib/H2.svelte';
    import PageLayout from '$lib/PageLayout.svelte';
    import PageSection from '$lib/PageSection.svelte';
    import Prose from '$lib/Prose.svelte';
    import ProductIcon from '$lib/ProductIcon.svelte';
    import PurchasePaddle from '$lib/PurchasePaddle.svelte';
    import { discount_data } from '$lib/stores/globals';
    import { format_price } from '$lib/utils/format_price';
    import ProductSchema from '$lib/ProductSchema.svelte';
    import PurchasePaddleGift from '$lib/PurchasePaddleGift.svelte';
    import { discount_applies } from '$lib/utils/discount';

    /** @type {import('./$types').PageData} */
    export let data;

    let { bundle } = data;

    const has_long_description = !!bundle.description_long?.trim();

    let localized_bundle_price_value = bundle.price?.price || 0;
    let localized_bundle_price_fx = bundle.price?.price_fx || 'USD';

    let promo_discount_applicable = false;

    function get_item_href(item) {
        if (item.type === 'course') {
            return `/courses/${item.slug}`;
        }

        if (item.type === 'worksheet') {
            return `/worksheets/${item.slug}`;
        }

        return item.url || null;
    }

    function get_item_price(item) {
        if (!item.price) {
            return '';
        }

        return format_price(
            item.price.price,
            item.price.price_fx || item.price.currency,
            'code'
        );
    }

    $: promo_discount_applicable = discount_applies(
        $discount_data,
        bundle.price,
        localized_bundle_price_fx
    );
</script>

<ProductSchema product={bundle} />

<PageLayout>
    <Breadcrumbs />
    <PageSection>
        <H1 class="mb-4">{bundle.title}</H1>
        <div
            class="grid gap-8 xl:grid-cols-[minmax(0,1fr)_auto] lg:auto-rows-min lg:items-start lg:gap-x-12 lg:gap-y-6"
        >
            <div class="lg:col-start-1 lg:row-start-1">
                <Prose xl class="prose-p:text-balance">
                    {@html bundle.description}
                </Prose>

                <div class="flex flex-wrap items-center gap-3 my-4">
                    {#if !bundle.purchased}
                        <PurchasePaddle
                            product={bundle}
                            cta="Buy Bundle"
                            cta_free="Unlock for free"
                            stopPropagation
                            feature="bundle-page-top"
                            style="small"
                            bind:price_effective_value={localized_bundle_price_value}
                            bind:price_fx={localized_bundle_price_fx}
                        />
                    {/if}
                    <PurchasePaddleGift
                        product={bundle}
                        stopPropagation
                        feature="bundle-page-top"
                        style="small"
                    />
                </div>
            </div>

            <div
                class="w-full lg:max-w-sm flex-shrink-0 flex-grow-0 lg:col-start-2 lg:row-span-2"
            >
                <Card>
                    <H2 class="px-4 py-2" sm>In this bundle</H2>
                    <table
                        class="w-full text-left border-t border-border-default"
                    >
                        <tbody>
                            {#each bundle.items as item}
                                {@const item_href = get_item_href(item)}
                                <tr
                                    class="border-b border-border-muted last:border-b-0"
                                >
                                    <td class="px-2 py-3 align-top">
                                        <div class="flex items-center gap-3">
                                            <ProductIcon
                                                product={item}
                                                class="!w-12 !h-12 !aspect-auto"
                                            />
                                            <div>
                                                {#if item_href}
                                                    <a
                                                        href={item_href}
                                                        class="link"
                                                    >
                                                        {@html item.title}
                                                    </a>
                                                {:else}
                                                    {@html item.title}
                                                {/if}
                                            </div>
                                        </div>
                                    </td>
                                    <td
                                        class="px-2 py-3 text-right align-middle font-extrabold whitespace-nowrap"
                                    >
                                        {get_item_price(item)}
                                    </td>
                                </tr>
                            {/each}
                        </tbody>
                        {#if !bundle.purchased}
                            <tfoot>
                                <tr class="border-t border-border-default">
                                    <th
                                        scope="row"
                                        class="px-2 pt-3 pb-1 font-medium text-right"
                                    >
                                        Bundle Value
                                    </th>
                                    <td
                                        class="px-2 pt-3 pb-1 text-right font-extrabold whitespace-nowrap"
                                    >
                                        {format_price(
                                            bundle.price_items.price,
                                            bundle.price_items.price_fx,
                                            'code'
                                        )}
                                    </td>
                                </tr>
                                {#if bundle.discount > 0}
                                    <tr>
                                        <th
                                            scope="row"
                                            class="px-2 py-1 font-medium text-right"
                                        >
                                            Bundle Discount
                                        </th>
                                        <td
                                            class="px-2 py-1 text-right whitespace-nowrap"
                                        >
                                            {@html bundle.discount_percent}
                                        </td>
                                    </tr>
                                {/if}
                                {#if promo_discount_applicable && $discount_data.amount}
                                    <tr>
                                        <th
                                            scope="row"
                                            class="px-2 py-1 font-medium text-right"
                                        >
                                            Promo Code
                                        </th>
                                        <td
                                            class="px-2 py-1 text-right whitespace-nowrap"
                                        >
                                            &minus;{$discount_data.type ===
                                            'percentage'
                                                ? `${$discount_data.amount}%`
                                                : format_price(
                                                      $discount_data.amount,
                                                      $discount_data.currency_code,
                                                      'code'
                                                  )}
                                        </td>
                                    </tr>
                                {/if}
                                <tr class="font-extrabold">
                                    <th
                                        scope="row"
                                        class="px-2 pt-1 pb-3 text-right"
                                    >
                                        Your Price
                                    </th>
                                    <td
                                        class="px-2 pt-1 pb-3 text-right whitespace-nowrap"
                                    >
                                        {format_price(
                                            localized_bundle_price_value,
                                            localized_bundle_price_fx,
                                            'code'
                                        )}
                                    </td>
                                </tr>
                            </tfoot>
                        {/if}
                    </table>
                </Card>
            </div>

            <div class="lg:col-start-1 lg:row-start-2">
                <Prose>
                    {@html bundle.description_long}
                </Prose>
            </div>
        </div>

        {#if !bundle.purchased}
            <div
                class="flex flex-wrap items-center gap-3 mt-8 mb-12 {has_long_description
                    ? ''
                    : 'lg:hidden'}"
            >
                <PurchasePaddle
                    product={bundle}
                    cta="Buy Bundle"
                    cta_free="Unlock for free"
                    stopPropagation
                    feature="bundle-page-bottom"
                    style="small"
                />
                <PurchasePaddleGift
                    product={bundle}
                    stopPropagation
                    feature="bundle-page-bottom"
                    style="small"
                />
            </div>
        {/if}
    </PageSection>

    {#if data.faq}
        <PageSection class="my-8">
            <Faq2 />
        </PageSection>
    {/if}
</PageLayout>
